import * as React from 'react';

import { config } from '@rematter/config';
import { Button } from '@rematter/ui-web';
import { LucideAlertCircle, LucideRefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * A more compact error boundary for use within sections of the app
 * rather than at the top level
 */
export class SectionErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (config.isDevelopment) {
      console.error('Section error caught:', error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-failure/20 bg-failure/5 p-6">
          <div className="flex items-start gap-3">
            <LucideAlertCircle className="mt-0.5 size-5 shrink-0 text-failure" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-failure/90">
                {this.props.fallbackMessage ||
                  'Something went wrong in this section'}
              </h3>
              {config.isDevelopment && this.state.error && (
                <p className="mt-1 text-xs text-failure/70">
                  {this.state.error.message}
                </p>
              )}
              <Button
                variant="outline"
                color="failure"
                className="mt-3"
                onClick={this.resetError}
              >
                <LucideRefreshCw className="mr-1 size-3" />
                Try Again
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
